/**
 * Typed client for the LoxLink API.
 *
 * Sessions are cookie-based, so every call sends credentials. State-changing auth calls
 * additionally need an Origin header — the server enforces it as CSRF protection, and the
 * browser supplies it automatically for same-origin requests.
 */

export type Role = 'admin' | 'viewer';
export type ExtractorKind = 'json' | 'css' | 'xml' | 'regex' | 'const' | 'series';
export type SourceKind = 'json' | 'html' | 'xml' | 'text';
export type DataType = 'number' | 'bool' | 'text';

export interface Step {
  op: string;
  [key: string]: unknown;
}

export interface Field {
  id: string;
  source_id: string;
  key: string;
  label: string;
  unit: string;
  extractor: ExtractorKind;
  expr: string;
  attr: string;
  time_expr: string;
  transforms: Step[];
  datatype: DataType;
  fallback: string | null;
  position: number;
  value: string | null;
  value_at: string | null;
  raw_sample: string | null;
  last_ok: boolean | null;
  last_error: string | null;
}

export interface Source {
  id: string;
  slug: string;
  name: string;
  notes: string;
  url: string | null;
  method: string;
  headers: Record<string, string>;
  body: string | null;
  kind: SourceKind;
  /** 'poll' fetches on a schedule; 'push' waits for an inbound webhook. */
  mode: 'poll' | 'push' | 'mqtt';
  /** mqtt only: the broker and topic filter this source subscribes to. */
  broker_id: string | null;
  topic: string;
  /** Write credential for a push source; null for a polling one. */
  in_token: string | null;
  last_payload: string | null;
  timezone: string;
  interval_seconds: number;
  enabled: boolean;
  out_token: string;
  last_run_at: string | null;
  last_success_at: string | null;
  last_status: number | null;
  last_duration_ms: number | null;
  last_error: string | null;
  consecutive_failures: number;
  fields: Field[];
}

export interface Membership {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  role: Role;
}

export interface Me {
  user: { id: string; email: string; name: string; isOperator: boolean };
  memberships: Membership[];
  activeTenantId: string | null;
  role: Role | null;
}

export interface StepArgDef {
  name: string;
  type: 'string' | 'number' | 'json';
  required?: boolean;
  summary: string;
}

export interface StepDef {
  op: string;
  group: string;
  summary: string;
  args: StepArgDef[];
}

export interface Meta {
  stage: number;
  steps: StepDef[];
  extractors: ExtractorKind[];
  sourceKinds: SourceKind[];
  datatypes: DataType[];
  minIntervalSeconds: number;
  publicUrl: string | null;
  publicUrlInsecure: string | null;
}

export interface PathHint {
  path: string;
  value: string | number | boolean | null;
  type: string;
}

export interface PreviewFieldResult {
  key: string;
  ok: boolean;
  value?: string;
  raw?: unknown;
  usedFallback?: boolean;
  error?: string;
}

export interface PreviewResult {
  ok: boolean;
  status?: number;
  durationMs?: number;
  contentType?: string;
  redirectChain?: string[];
  bytes?: number;
  truncated?: boolean;
  sample?: string;
  hints?: PathHint[];
  parseError?: string;
  error?: string;
  fields?: PreviewFieldResult[];
}

export interface PlannedCommand {
  key: string;
  title: string;
  check: string;
  analog: boolean;
  unit: string;
  currentValue: string | null;
  verified: boolean;
  extracted: number | null;
  lenient: boolean;
  error: string | null;
}

export interface SourcePlan {
  source: { id: string; slug: string; name: string };
  urls: { https: string; http: string; json: string; xml: string };
  pollingSeconds: number;
  payload: string;
  commands: PlannedCommand[];
  unsupported: { key: string; value: string | null; reason: string }[];
  xml: string;
  warnings: string[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
  }
}

/** Set when acting on a specific installation; the server falls back to the only one. */
let activeTenant: string | null = null;
export function setActiveTenant(tenantId: string | null): void {
  activeTenant = tenantId;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('content-type', 'application/json');
  if (activeTenant) headers.set('x-loxlink-tenant', activeTenant);

  const res = await fetch(path, { ...init, headers, credentials: 'include' });

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const isJson = res.headers.get('content-type')?.includes('json');
  const data = isJson && text ? JSON.parse(text) : text;

  if (!res.ok) {
    const message = typeof data === 'object' && data && 'error' in data ? String(data.error) : res.statusText;
    throw new ApiError(res.status, message || `request failed (${res.status})`);
  }
  return data as T;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export const api = {
  me: () => call<Me>('/api/me'),
  meta: () => call<Meta>('/api/meta'),

  signIn: (email: string, password: string) =>
    call<unknown>('/api/auth/sign-in/email', { method: 'POST', ...json({ email, password }) }),
  signOut: () => call<unknown>('/api/auth/sign-out', { method: 'POST', body: '{}' }),

  sources: () => call<{ sources: Source[] }>('/api/sources').then((r) => r.sources),
  source: (id: string) => call<{ source: Source; fields: Field[] }>(`/api/sources/${id}`),

  createSource: (body: Partial<Source>) =>
    call<{ source: Source }>('/api/sources', { method: 'POST', ...json(body) }).then((r) => r.source),
  updateSource: (id: string, body: Partial<Source>) =>
    call<{ source: Source }>(`/api/sources/${id}`, { method: 'PATCH', ...json(body) }).then((r) => r.source),
  deleteSource: (id: string) => call<unknown>(`/api/sources/${id}`, { method: 'DELETE' }),
  runSource: (id: string) => call<{ ok: boolean; error?: string; fields: PreviewFieldResult[] }>(
    `/api/sources/${id}/run`,
    { method: 'POST', body: '{}' }
  ),
  rotateToken: (id: string) =>
    call<{ source: Source; warning: string }>(`/api/sources/${id}/rotate-token`, { method: 'POST', body: '{}' }),

  createField: (sourceId: string, body: Partial<Field>) =>
    call<{ field: Field }>(`/api/sources/${sourceId}/fields`, { method: 'POST', ...json(body) }).then((r) => r.field),
  updateField: (id: string, body: Partial<Field>) =>
    call<{ field: Field }>(`/api/fields/${id}`, { method: 'PATCH', ...json(body) }).then((r) => r.field),
  deleteField: (id: string) => call<unknown>(`/api/fields/${id}`, { method: 'DELETE' }),

  preview: (body: {
    /** Fetch this URL, or supply `payload` for a push source that has no URL. */
    url?: string;
    payload?: string;
    kind?: SourceKind;
    method?: string;
    headers?: Record<string, string>;
    timezone?: string;
    fields?: Partial<Field>[];
  }) => call<PreviewResult>('/api/preview', { method: 'POST', ...json(body) }),

  plan: (id: string) => call<SourcePlan>(`/api/sources/${id}/plan`),
  templateUrl: (id: string) => `/api/sources/${id}/template.xml`,
};
